// ABOUTME: Firestore access for the per-user preferences document.
// ABOUTME: Lives at users/{uid}/settings/prefs; shape is validated by firestore.rules.

import { deleteField, doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "./firebase";
import type { UserPrefs } from "../utils/prefs";

function prefsRef(userId: string) {
  return doc(db, `users/${userId}/settings`, "prefs");
}

export const prefsService = {
  /**
   * Streams the raw preferences document. The callback receives `null` when
   * no document exists yet, which is how a first sign-in is detected.
   */
  subscribe(
    userId: string,
    onData: (data: Record<string, unknown> | null) => void,
    onError: (error: Error) => void
  ): () => void {
    return onSnapshot(
      prefsRef(userId),
      (snap) => {
        if (snap.exists()) {
          onData(snap.data() as Record<string, unknown>);
          return;
        }
        // Only the server can say the document does not exist. A cache miss
        // on a fresh, offline device is not a first sign-in, and seeding
        // from it would overwrite the account's real preferences later.
        if (!snap.metadata?.fromCache) onData(null);
      },
      onError
    );
  },

  /**
   * Merges a partial update so concurrent devices only overwrite the keys they
   * changed. Documents written before `palette` was retired still hold it, and
   * the rules reject any write that leaves it in place, so every save deletes it.
   */
  async save(userId: string, patch: Partial<UserPrefs>): Promise<void> {
    await setDoc(
      prefsRef(userId),
      { ...patch, palette: deleteField(), updatedAt: Date.now() },
      { merge: true }
    );
  },
};
